import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from "react-i18next";
import "../i18n";

const ServicesSection = () => {
  const { t } = useTranslation(['common', 'services']);

  const services = [
    {
      title: t("services:aidata.title"),
      subtitle: t("services:aidata.subtitle"),
      description: t("services:aidata.description"),
      link: '#',
      bgImage: '/images/services/AI-1.svg',
      bgImageSet: '/images/services/Ai-2.svg',
      circleImage: '/images/services/Ai-2.svg',
    },
    {
      title: t("services:metaverse.title"),
      subtitle: t("services:metaverse.subtitle"),
      description: t("services:metaverse.description"),
      link: '#',
      bgImage: '/images/services/Metaverse-1.svg',
      bgImageSet: '/images/services/Metaverse-2.svg',
      circleImage: '/images/services/Metaverse-1.svg',
    },
    {
      title: t("services:blockchain.title"),
      subtitle: t("services:blockchain.subtitle"),
      description: t("services:blockchain.description"),
      link: '#',
      bgImage: '/images/services/Blockchain-1.svg',
      bgImageSet: '/images/services/Blockchain.svg',
      circleImage: '/images/services/Blockchain-1.svg',
    },
    {
      title: t("services:iot.title"),
      subtitle: t("services:iot.subtitle"),
      description: t("services:iot.description"),
      link: '#',
      bgImage: '/images/services/IOT-1.svg',
      bgImageSet: '/images/services/IOT-2.svg',
      circleImage: '/images/services/IOT-1.svg',
    },
    {
      title: t("services:cybersecurity.title"),
      subtitle: t("services:cybersecurity.subtitle"),
      description: t("services:cybersecurity.description"),
      link: '#',
      bgImage: '/images/services/Cyber-Security-1.svg',
      bgImageSet: '/images/services/Cyber-Security-2.svg',
      circleImage: '/images/services/Cyber-Security-1.svg',
    },
    {
      title: t("services:consultingStrategy.title"),
      subtitle: t("services:consultingStrategy.subtitle"),
      description: t("services:consultingStrategy.description"),
      link: '#',
      bgImage: '/images/services/Consulting-1.svg',
      bgImageSet: '/images/services/Consulting-2.svg',
      circleImage: '/images/services/Consulting-1.svg',
    },
  ];

  const slideUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const hoverVariants = {
    hidden: {
      opacity: 0,
      x: '3rem',
      y: '3rem',
      scale: 0.8,
      rotateX: 8,
      rotateY: -16,
      rotateZ: 0,
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      rotateX: 8,
      rotateY: -16,
      rotateZ: 0,
      transition: { duration: 0.3, ease: 'easeOut' },
    },
  };

  const ServiceCard = ({ service }) => {
    const cardRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const [prevMousePos, setPrevMousePos] = useState({ x: 0, y: 0 });
    const [moveTransform, setMoveTransform] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e) => {
      const rect = cardRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const deltaX = mouseX - prevMousePos.x;
      const deltaY = mouseY - prevMousePos.y;

      setPrevMousePos({ x: mouseX, y: mouseY });

      const moveX = deltaX * 0.05;
      const moveY = deltaY * 0.05;

      setMoveTransform({
        x: Math.max(-10, Math.min(10, moveTransform.x + moveX)),
        y: Math.max(-10, Math.min(10, moveTransform.y + moveY)),
      });
    };

    const handleMouseEnter = () => {
      setIsHovered(true);
      const rect = cardRef.current.getBoundingClientRect();
      setPrevMousePos({
        x: window.event.clientX - rect.left,
        y: window.event.clientY - rect.top,
      });
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      setMoveTransform({ x: 0, y: 0 });
      setPrevMousePos({ x: 0, y: 0 });
    };

    return (
      <motion.div
        variants={slideUp}
        className="w-full"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={cardRef}
      >
        <div className="relative flex flex-col justify-end items-start text-white text-left rounded-3xl shadow-lg border border-white overflow-hidden h-[300px]">
          {/* Default Card */}
          <div
            className="absolute inset-0 p-6 bg-cover bg-center transition-opacity duration-300"
            style={{
              backgroundImage: service.bgImage ? `url(${service.bgImage})` : 'none',
              opacity: isHovered ? 0 : 1,
            }}
          >
            <h2 className="text-2xl font-bold uppercase mb-2">{service.title}</h2>
            <h5 className="">{service.subtitle}</h5>
          </div>

          {/* Hover Card */}
          <motion.div
            // href={service.link}
            className="absolute inset-0 flex flex-col justify-between items-center text-center bg-cover bg-center rounded-3xl"
            animate={isHovered ? 'visible' : 'hidden'}
            variants={hoverVariants}
            style={{ transformStyle: 'preserve-3d' }}
          >
            <motion.div
              className="relative w-24 h-24 mt-4 rounded-full overflow-hidden border-4 border-white"
              animate={{
                x: moveTransform.x,
                y: moveTransform.y,
                transition: { type: 'spring', stiffness: 100, damping: 20 },
              }}
            >
              <img
                src={service.circleImage}
                srcSet={service.bgImageSet}
                alt={service.title}
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              className="relative p-6 bg-gradient-to-t from-black/80 to-transparent w-full"
              animate={{
                x: moveTransform.x * 0.5,
                y: moveTransform.y * 0.5,
                transition: { type: 'spring', stiffness: 100, damping: 20 },
              }}
            >
              <h4 className="text-3xl font-bold uppercase mb-2">{service.title}</h4>
              <h5 className="text-sm">{service.description}</h5>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    );
  };

  return (
    <section className="py-4 sm:py-4 md:py-16 lg:py-16 px-2">
      <div className="container mx-auto px-6 lg:px-20">
        <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
          {t("services:services")}
        </h1>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
